import React, { useState } from 'react';
import {
  Compass,
  ClipboardCheck,
  Layers,
  Handshake,
  Building2,
  ArrowRight,
  Sparkles,
  Mail,
  Check,
  ChevronDown,
  Ruler,
  Users,
  Home,
  BookOpen,
  BarChart3,
  ShieldCheck,
  FileText,
  Target,
  Lightbulb,
  CreditCard,
  Heart,
} from 'lucide-react';
import SubpageShell from './SubpageShell';
import JsonLd, { SITE, PROVIDER } from '../components/JsonLd';
import SEO from '../components/SEO';
import SlotPickerModal from '../components/SlotPickerModal';
import QualifiedEnquiryForm from '../components/QualifiedEnquiryForm';

const DISCOVERY_FIELDS = [
  { name: 'name', label: 'Your name', required: true, autoComplete: 'name' },
  { name: 'role', label: 'Your role', required: true, autoComplete: 'organization-title' },
  { name: 'organisation', label: 'Organisation', required: true, autoComplete: 'organization' },
  { name: 'sector', label: 'Sector', required: true, placeholder: 'e.g. Aviation, Housing, Education' },
  { name: 'email', label: 'Work email', type: 'email', required: true, autoComplete: 'email' },
  { name: 'phone', label: 'Phone (optional)', type: 'tel', autoComplete: 'tel', maxLength: 40 },
  {
    name: 'timeframe', label: 'When are you hoping to start?', type: 'select', required: true,
    options: [['0-3-months', 'Within 3 months'], ['3-6-months', '3–6 months'], ['6-12-months', '6–12 months'], ['exploring', 'Just exploring']],
  },
  { name: 'objective', label: 'What would you like the session to help with?', type: 'textarea', required: true, minLength: 10, maxLength: 2000, wide: true },
];
import '../styles/Advisory.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* ============================================================
   /advisory — N3URO WORLD® Advisory
   ------------------------------------------------------------
   Five flagship service "products", an additional-services
   catalogue, and a closing commercial CTA. Every card ties
   into the wider brand palette (cyan / purple / pink / gold).
   ============================================================ */

const SERVICES = [
  {
    id: 'strategic-discovery',
    icon: Compass,
    accent: '#5BE9FF',
    title: 'Executive Discovery Session',
    price: '£1,250',
    priceCadence: '',
    stripeLookupKey: 'advisory_discovery_gbp',
    tagline:
      'A focused executive session designed to explore your organisation’s objectives, identify opportunities and establish a strategic direction before wider engagement.',
    includesLabel: 'Your session includes',
    includes: [
      '2-Hour Executive Discovery Session',
      'Executive Review of Your Organisation, Project or Opportunity',
      'Priority Recommendations',
      'Identification of Opportunities & Key Risks',
      'Executive Discovery Summary & Recommended Next Steps',
      '30-Day Follow-Up Session',
    ],
    footnoteLabel: 'Best for',
    footnote:
      'Organisations exploring a new project or seeking independent strategic advice.',
    ctaLabel: 'Book a session',
  },
  {
    id: 'infrastructure-review',
    icon: ClipboardCheck,
    accent: '#C77DFF',
    title: 'Infrastructure Review',
    price: 'From £7,500',
    priceCadence: '',
    tagline:
      'A comprehensive review of your organisation’s environments, services and family experience, providing practical recommendations to strengthen neuro-inclusive infrastructure.',
    includesLabel: 'Your review includes',
    includes: [
      'Site & Infrastructure Assessment',
      'Family Journey & User Experience Review',
      'Accessibility & Inclusion Assessment',
      'Stakeholder Consultation',
      'Executive Review Report',
      'Priority Action Framework',
      'Executive Presentation & Recommendations',
    ],
    footnoteLabel: 'Typical duration',
    footnote: '2 – 4 weeks',
    ctaLabel: 'Request a review',
  },
  {
    id: 'strategic-programme',
    icon: Layers,
    accent: '#FF6BC2',
    title: 'Infrastructure Transformation Programme',
    price: 'From £20,000',
    priceCadence: '',
    tagline:
      'End-to-end strategic support for organisations planning, designing or transforming neuro-inclusive family infrastructure.',
    includesLabel: 'Your programme includes',
    includes: [
      'Programme Framework',
      'Implementation Roadmap',
      'Infrastructure Development Plan',
      'Stakeholder Engagement Framework',
      'Governance Recommendations',
      'Executive Presentation',
      'Implementation Priorities',
      'Ongoing Executive Advisory throughout the programme',
    ],
    footnoteLabel: 'Typical duration',
    footnote: '2 – 6 months',
    ctaLabel: 'Plan a programme',
  },
  {
    id: 'executive-partnership',
    icon: Handshake,
    accent: '#FFE066',
    title: 'Executive Advisory Partnership',
    price: 'From £3,000',
    priceCadence: 'per month',
    tagline:
      'An ongoing strategic advisory partnership providing executive-level support throughout planning, delivery and organisational development.',
    includesLabel: 'Your partnership includes',
    includes: [
      'Monthly Executive Advisory Sessions',
      'Executive Decision Support',
      'Programme Reviews',
      'Priority Executive Access',
      'Executive Document & Proposal Review',
      'Executive Sounding Board',
      'Quarterly Planning & Review',
      'Responsive Executive Guidance Between Sessions',
    ],
    footnoteLabel: 'Recommended commitment',
    footnote: '6 months',
    ctaLabel: 'Start a partnership',
  },
  {
    id: 'enterprise-advisory',
    icon: Building2,
    accent: '#7CE38B',
    title: 'Enterprise Advisory',
    price: 'Bespoke Proposal',
    priceCadence: '',
    tagline:
      'Executive advisory for complex, multi-site or national programmes requiring tailored support.',
    includes: [
      'Organisation-wide Planning',
      'Multi-site Infrastructure Reviews',
      'Programme Governance',
      'Executive Facilitation',
      'Framework Development',
      'Long-term Advisory Partnerships',
      'National Implementation Support',
    ],
    footnoteLabel: 'Pricing',
    footnote: 'By proposal following an initial consultation.',
    ctaLabel: 'Request a proposal',
  },
];

const ADDITIONAL = [
  { icon: Layers,       label: 'Neuro-Inclusive Infrastructure Strategy' },
  { icon: Users,        label: 'Family Experience Strategy' },
  { icon: Home,         label: 'Housing & Residential Advisory' },
  { icon: Ruler,        label: 'Inclusive Design Reviews' },
  { icon: Compass,      label: 'Masterplanning Support' },
  { icon: Handshake,    label: 'Stakeholder Engagement' },
  { icon: BookOpen,     label: 'Executive Workshops' },
  { icon: ShieldCheck,  label: 'Policy & Framework Development' },
  { icon: Lightbulb,    label: 'Pilot Programme Design' },
  { icon: BarChart3,    label: 'Impact Measurement & Evaluation' },
  { icon: FileText,     label: 'Strategic Reporting' },
];

const Advisory = () => {
  const [pickerService, setPickerService] = useState(null);

  const mailtoFor = (service) =>
    `mailto:hello@n3uro.world?subject=${encodeURIComponent(
      `N3URO Advisory enquiry — ${service}`,
    )}&body=${encodeURIComponent(
      `Hello N3URO WORLD® Advisory team,\n\nI would like to enquire about: ${service}.\n\n[Your name]\n[Organisation]\n[Project or context]\n`,
    )}`;

  const openBookingPicker = (service) => {
    setPickerService(service);
  };

  return (
    <SubpageShell currentPath="/advisory">
      <SEO
        title="N3URO Advisory"
        description="Strategic advisory for Neuro-Inclusive Family Infrastructure — Executive Discovery Sessions, Infrastructure Reviews, Strategic Programmes and long-term Executive Advisory Partnerships."
        path="/advisory"
      />
      <JsonLd data={{ '@type': 'Service', name: 'N3URO Advisory', url: `${SITE}/advisory`, description: 'Organisational route for exploring how neuro-inclusive family access can be built into an environment, development or portfolio.', provider: PROVIDER, areaServed: 'GB' }} />
      <main className="adv-page" data-testid="advisory-page">
        {/* Native, responsive hero — image AT TOP followed by the standard
            rainbow-gradient title + white lede pattern used across the site.
            Never blurs, never distorts, same voice on every page. */}
        <section className="adv-hero" data-testid="advisory-hero">
          <figure className="adv-hero-visual" aria-hidden="true">
            <img decoding="async"
              src="/img/advisory-phone-card.webp"
              alt=""
              className="adv-hero-phone"
              loading="eager"
              width="510"
              height="742"
            />
          </figure>

          <div className="adv-hero-copy">
            <span className="subpage-eyebrow" data-testid="advisory-title">N3URO Advisory</span>
            <h1 className="subpage-title">
              Strategic advisory for neuro-inclusive family infrastructure.
            </h1>
            <p className="subpage-lede">
              We partner with organisations to design, review and deliver
              environments, services and communities that work better for
              neurodivergent children, young people and their families.
            </p>
            <div className="adv-hero-cta-row">
              <a
                href="#discovery-enquiry"
                className="adv-hero-cta adv-hero-cta--primary"
                data-cta="advisory-hero-request-discovery"
                data-testid="advisory-hero-cta-request"
              >
                <span>Request a Discovery Session</span>
                <ArrowRight size={16} strokeWidth={2.4} />
              </a>
              <a
                href="#strategic-discovery"
                className="adv-hero-cta adv-hero-cta--ghost"
                data-cta="advisory-hero-whats-included"
                data-testid="advisory-hero-cta-details"
              >
                See what&rsquo;s included
              </a>
            </div>
            <p className="adv-hero-cta-note">
              <Sparkles size={12} strokeWidth={2} aria-hidden="true" />
              Executive Discovery Session from £1,250 &middot; Card payments coming soon
            </p>
          </div>

          <ul className="adv-hero-pillars" aria-label="Advisory principles">
            <li className="adv-hero-pillar" style={{ '--accent': '#5BE9FF' }}>
              <span className="adv-hero-pillar-icon" aria-hidden="true"><Target size={22} strokeWidth={1.75} /></span>
              <span className="adv-hero-pillar-text">STRATEGIC<br />PLANNING</span>
            </li>
            <li className="adv-hero-pillar" style={{ '--accent': '#C77DFF' }}>
              <span className="adv-hero-pillar-icon" aria-hidden="true"><Users size={22} strokeWidth={1.75} /></span>
              <span className="adv-hero-pillar-text">EXPERT<br />INSIGHT</span>
            </li>
            <li className="adv-hero-pillar" style={{ '--accent': '#5BE9FF' }}>
              <span className="adv-hero-pillar-icon" aria-hidden="true"><Building2 size={22} strokeWidth={1.75} /></span>
              <span className="adv-hero-pillar-text">REAL-WORLD<br />IMPACT</span>
            </li>
            <li className="adv-hero-pillar" style={{ '--accent': '#FF6BC2' }}>
              <span className="adv-hero-pillar-icon" aria-hidden="true"><Handshake size={22} strokeWidth={1.75} /></span>
              <span className="adv-hero-pillar-text">PARTNERSHIP<br />&amp; COLLABORATION</span>
            </li>
            <li className="adv-hero-pillar" style={{ '--accent': '#FFB166' }}>
              <span className="adv-hero-pillar-icon" aria-hidden="true"><Heart size={22} strokeWidth={1.75} /></span>
              <span className="adv-hero-pillar-text">INCLUSIVE<br />BY DESIGN</span>
            </li>
          </ul>

          <p className="adv-hero-tagline" aria-hidden="true">
            <span className="adv-hero-tag-cyan">EXPLORE.</span>{' '}
            <span className="adv-hero-tag-mag">REGULATE.</span>{' '}
            <span className="adv-hero-tag-gold">CONNECT.</span>
          </p>
        </section>

        {/* ============= 5 SERVICE PRODUCT CARDS ============= */}
        <section className="adv-discovery-enquiry" id="discovery-enquiry" aria-labelledby="adv-discovery-heading" data-testid="advisory-discovery-enquiry">
          <span className="subpage-eyebrow">Start here</span>
          <h2 id="adv-discovery-heading" className="adv-section-title">Request a Discovery Session</h2>
          <p className="subpage-lede">
            An Executive Discovery Session helps your leadership team understand where neuro-inclusive family
            access matters in your environment, and what to prioritise next.
          </p>
          <h3 className="adv-discovery-sub">What you leave with</h3>
          <ul className="conv-facts" data-testid="advisory-outcomes">
            <li><strong>Shared understanding</strong><span>Of where families may meet barriers in your environment or service</span></li>
            <li><strong>Priority opportunities</strong><span>Focused on your organisation&rsquo;s context and goals</span></li>
            <li><strong>Written summary</strong><span>Executive Discovery Summary &amp; Recommended Next Steps</span></li>
          </ul>
          <h3 className="adv-discovery-sub">How the introduction works</h3>
          <ol className="conv-steps" data-testid="advisory-process">
            <li><b>Step 1</b><strong>Send your enquiry</strong><p>Tell us about your organisation and what you&rsquo;d like to explore.</p></li>
            <li><b>Step 2</b><strong>Introductory conversation</strong><p>We confirm fit, scope and who should take part. No commitment.</p></li>
            <li><b>Step 3</b><strong>Your Discovery Session</strong><p>We agree a date and format together, followed by your written summary.</p></li>
          </ol>
          <QualifiedEnquiryForm
            id="advisory-discovery"
            endpoint="/api/advisory/discovery-enquiry"
            title="Discovery Session enquiry"
            intro="This is an enquiry, not a booking or payment. We'll reply to arrange an introductory conversation."
            fields={DISCOVERY_FIELDS}
            submitLabel="Request a Discovery Session"
            successTitle="Thank you — your Discovery Session request has been received."
            successBody="A member of the N3URO WORLD® team will reply to arrange an introductory conversation. Nothing has been booked or charged."
          />
        </section>

        <section className="adv-services" aria-labelledby="adv-services-heading">
          <h2 id="adv-services-heading" className="adv-section-title">
            Advisory Services
          </h2>

          <ol className="adv-cards" role="list">
            {SERVICES.map(
              (
              {
                id,
                icon: Icon,
                accent,
                title,
                price,
                priceCadence,
                tagline,
                includes,
                includesLabel,
                footnoteLabel,
                footnote,
                ctaLabel,
                stripeLookupKey,
              },
              idx,
              ) => (
                <li
                  key={id}
                  id={id}
                  className="adv-card"
                  style={{ '--accent': accent }}
                  data-testid={`adv-card-${id}`}
                >
                  <details className="adv-card-details">
                    <summary className="adv-card-summary">
                      <span className="adv-card-num" aria-hidden="true">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="adv-card-icon" aria-hidden="true">
                        <Icon size={22} strokeWidth={1.8} />
                      </span>
                      <div className="adv-card-heading">
                        <h3 className="adv-card-title">{title}</h3>
                        <p className="adv-card-tagline-inline">{tagline}</p>
                      </div>
                      <div className="adv-card-price-block">
                        <span className="adv-card-price-val">{price}</span>
                        {priceCadence && (
                          <span className="adv-card-price-cadence">
                            {priceCadence}
                          </span>
                        )}
                      </div>
                      <span className="adv-card-chevron" aria-hidden="true">
                        <ChevronDown size={20} strokeWidth={2.4} />
                      </span>
                    </summary>

                    <div className="adv-card-body">
                      <div className="adv-card-includes">
                        <p className="adv-card-includes-label">{includesLabel || 'Includes'}</p>
                        <ul>
                          {includes.map((line) => (
                            <li key={line}>
                              <span className="adv-check" aria-hidden="true">
                                <Check size={14} strokeWidth={2.6} />
                              </span>
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <p className="adv-card-footnote">
                        <strong>{footnoteLabel}:</strong> {footnote}
                      </p>

                      <div className="adv-card-cta-row">
                        {stripeLookupKey && (
                          <>
                            <a href="#discovery-enquiry" className="adv-card-cta adv-card-cta--pay" data-cta={`advisory-card-request-${id}`} data-testid={`adv-cta-request-${id}`}>
                              <span>Request a Discovery Session</span>
                              <ArrowRight size={16} strokeWidth={2.4} />
                            </a>
                            <button type="button" className="adv-card-cta adv-card-cta--enquire conv-btn-disabled" disabled aria-disabled="true" data-testid={`adv-cta-pay-${id}`}>
                              <CreditCard size={16} strokeWidth={2.2} />
                              <span>Card payments coming soon</span>
                            </button>
                          </>
                        )}
                        <a
                          className={
                            stripeLookupKey
                              ? 'adv-card-cta adv-card-cta--enquire'
                              : 'adv-card-cta'
                          }
                          href={mailtoFor(title)}
                          data-cta={`advisory-card-email-${id}`}
                          data-testid={`adv-cta-${id}`}
                        >
                          <span>
                            {stripeLookupKey ? 'Enquire by email' : ctaLabel}
                          </span>
                          <ArrowRight size={16} strokeWidth={2.4} />
                        </a>
                      </div>
                    </div>
                  </details>
                </li>
              ),
            )}
          </ol>

          <p className="adv-services-note" data-testid="adv-services-note">
            Every engagement is tailored to the organisation&rsquo;s objectives.
            Prices shown are starting fees for defined advisory services.
          </p>
        </section>

        {/* ============= ADDITIONAL SERVICES CATALOGUE ============= */}
        <section className="adv-additional" aria-labelledby="adv-additional-heading">
          <div className="adv-additional-head">
            <h2 id="adv-additional-heading" className="adv-section-title adv-section-title--left">
              Specialist Advisory Services
            </h2>
            <p className="adv-additional-sub">
              These can be commissioned independently or incorporated into a
              wider programme.
            </p>
          </div>
          <ul className="adv-additional-grid" role="list">
            {ADDITIONAL.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="adv-additional-item"
                data-testid={`adv-add-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                tabIndex={0}
              >
                <span className="adv-additional-icon" aria-hidden="true">
                  <Icon size={20} strokeWidth={1.8} />
                </span>
                <span className="adv-additional-label">{label}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ============= CLOSING CTA ============= */}
        <section className="adv-close" aria-labelledby="adv-close-heading">
          <div className="adv-close-inner">
            <span className="adv-close-icon" aria-hidden="true">
              <Target size={32} strokeWidth={1.8} />
            </span>
            <h2 id="adv-close-heading" className="adv-close-title">
              Ready to discuss your project?
            </h2>
            <p className="adv-close-body">
              Whether you&rsquo;re planning a new development, reviewing an
              existing environment or exploring a future initiative, N3URO
              WORLD Advisory provides strategic guidance to help organisations
              create more inclusive family infrastructure.
            </p>
            <div className="adv-close-btns">
              <a
                className="adv-cta-primary"
                href={mailtoFor('Executive Discovery Session')}
                data-testid="adv-close-book"
              >
                <Sparkles size={16} strokeWidth={2.2} />
                Book an Executive Discovery Session
                <ArrowRight size={16} strokeWidth={2.4} />
              </a>
              <a
                href="mailto:hello@n3uro.world?subject=N3URO%20Advisory%20enquiry"
                className="adv-cta-ghost"
                data-testid="adv-close-email"
              >
                <Mail size={16} strokeWidth={2} />
                hello@n3uro.world
              </a>
            </div>
          </div>
        </section>
      </main>

      <SlotPickerModal
        open={!!pickerService}
        lookupKey={pickerService?.lookupKey}
        serviceLabel={pickerService?.title}
        priceLabel={pickerService?.price}
        onClose={() => setPickerService(null)}
      />
    </SubpageShell>
  );
};

export default Advisory;
